import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle, Building2, Calendar, Check, ChevronLeft, Circle, Download, FileText,
  Loader2, Paperclip, ShieldCheck, Upload, User as UserIcon, UserCheck,
} from "lucide-react";
import { useContractDetail } from "@/hooks/useContracts";
import { attachmentDownloadUrl } from "@/api/contracts";
import { downloadAttachment } from "@/api/taskAttachments";
import { AnnexWizardModal } from "@/components/contract/annex/AnnexWizardModal";
import { UploadDialog } from "@/components/contract/AttachmentsList";
import { CommercialTermsBlock } from "@/components/contract/CommercialTermsBlock";
import { DraftTermsEditor } from "@/components/contract/DraftTermsEditor";
import { RenewalDrawer } from "@/components/contract/RenewalDrawer";
import { CreateInternalRequestDrawer } from "@/components/requests/CreateInternalRequestDrawer";
import type { ContractDetailResponse, ContractState } from "@/api/types";

// BUNDLE 1 STEP 5 — Contract custom view v0.3 detail (2-col + lifecycle-timeline +
// source/lifecycle-aware commercial-terms). WS-2: єдиний detail-шлях (legacy F4
// ContractDetailPage і флаг видалені). No-amount invariant: only creditLimit
// (commercial term) is shown — never a contractAmount/totalValue.
//
// Contract UNIFICATION — ONE detail component for 3 contexts:
//   1. standalone /contracts/:id (param-driven, default).
//   2. Client360 /clients/:holdingId/contracts/:contractId (props.contractId + backTo).
//   3. create → draft → lands here with an editable commercial-terms surface
//      (DraftTermsEditor) when allowedActions.canEditDraft (crm_managed draft).
// sync_1c rows stay read-only (canEditDraft=false → read-only CommercialTermsBlock).

const TYPE_LABEL: Record<string, string> = {
  supply_agreement: "Договір поставки", dealer_agreement: "Дилерський договір",
  marketing_agreement: "Маркетинговий договір", nda: "NDA", other: "Інший",
};

function TLStep({ label, done, current }: { label: string; done?: boolean; current?: boolean }) {
  return (
    <div className="flex items-center gap-1.5">
      {done ? <Check className="h-3.5 w-3.5 text-primary" />
        : current ? <Circle className="h-3.5 w-3.5 fill-warning-foreground text-warning-foreground" />
        : <Circle className="h-3.5 w-3.5 text-muted-foreground/40" />}
      <span className={`text-xs ${done || current ? "text-foreground" : "text-muted-foreground/60"}`}>{label}</span>
    </div>
  );
}

function Section({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border bg-card">
      <div className="border-b px-4 py-2 text-sm font-medium flex items-center gap-2">{icon}{title}</div>
      <div className="space-y-2 p-4">{children}</div>
    </div>
  );
}

function KV({ k, v, hl, empty }: { k: string; v?: string | null; hl?: boolean; empty?: string }) {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <span className="text-xs text-muted-foreground">{k}</span>
      {v ? <span className={`text-right ${hl ? "font-medium text-warning-foreground" : "text-foreground"}`}>{v}</span>
        : <span className="text-right text-xs italic text-muted-foreground/50">{empty ?? "—"}</span>}
    </div>
  );
}

/** Смоук-фікс Igor: живий рядок «Документи» — імʼя-клік=download, явна ⬇, поруч «підписаний скан». */
function DocRow({ fileName, meta, downloading, onDownload, onUploadScan }: {
  fileName: string;
  meta?: string;
  downloading: boolean;
  onDownload: () => void;
  onUploadScan?: () => void;
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <FileText className="h-3.5 w-3.5 shrink-0 text-primary" />
      <button
        type="button"
        disabled={downloading}
        onClick={onDownload}
        className="truncate text-left hover:text-primary hover:underline disabled:opacity-50"
        title={`Скачати ${fileName}`}
      >
        {fileName}
      </button>
      {meta && <span className="whitespace-nowrap text-xs text-muted-foreground">{meta}</span>}
      <span className="flex-1" />
      <button
        type="button"
        disabled={downloading}
        onClick={onDownload}
        className="inline-flex shrink-0 items-center justify-center rounded-md p-1 text-primary hover:bg-primary/10 disabled:opacity-50"
        aria-label={`Скачати ${fileName}`}
      >
        {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
      </button>
      {onUploadScan && (
        <button
          type="button"
          onClick={onUploadScan}
          className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border px-2 py-0.5 text-xs text-muted-foreground hover:border-primary/40 hover:text-primary"
          title="Наступний крок: роздрукувати, підписати, завантажити скан"
        >
          <Upload className="h-3 w-3" />Підписаний скан
        </button>
      )}
    </div>
  );
}

const TIMELINE_ACTIVE: ContractState[] = ["active"];
const TIMELINE_SIGNED: ContractState[] = ["signed", "active", "terminated", "expired", "archived"];
const TIMELINE_APPROVAL: ContractState[] = ["pending_approval_sales", "pending_approval_legal"];

interface ContractDetailV2Props {
  /** Override the route :id (Client360 passes its :contractId). */
  contractId?: string;
  /** Where the back link / not-found CTA navigates (default: the contracts list). */
  backTo?: string;
}

export function ContractDetailV2({ contractId, backTo = "/contracts" }: ContractDetailV2Props = {}) {
  const params = useParams<{ id: string }>();
  const id = contractId ?? params.id;
  const navigate = useNavigate();
  const query = useContractDetail(id ?? null);
  const [renewOpen, setRenewOpen] = useState(false);
  const [ctcOpen, setCtcOpen] = useState(false); // G1-S3 change-request
  const [annexOpen, setAnnexOpen] = useState(false); // D-143 майстер додаткової угоди
  // смоук-фікс Igor: живі рядки «Документи» — download + «підписаний скан» (§0/§20.6)
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [scanUploadOpen, setScanUploadOpen] = useState(false);

  if (query.isPending) {
    return <div className="mx-auto max-w-4xl px-4 py-6"><div className="h-40 animate-pulse rounded-xl bg-muted" /></div>;
  }
  if (query.isError || !query.data) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-12 text-center">
        <ShieldCheck className="mx-auto h-7 w-7 text-muted-foreground/60" />
        <h1 className="mt-3 text-lg font-semibold">Договір не знайдено або немає доступу</h1>
        <button onClick={() => navigate(backTo)} className="mt-3 rounded-md border px-3 py-2 text-sm">До списку</button>
      </div>
    );
  }

  const c: ContractDetailResponse = query.data;
  const type = TYPE_LABEL[c.contractType] ?? c.contractType;
  const st = c.currentState;
  const isDraft = st === "draft" || st === "rejected";
  const isApproval = TIMELINE_APPROVAL.includes(st);
  const isTerm = st === "terminated" || st === "expired";
  const expiring = c.daysToExpiry !== null && c.daysToExpiry >= 0 && c.daysToExpiry <= 30;
  const client = c.holding?.name || c.account?.name || "—";
  // Editable draft surface — crm_managed draft (sync_1c → canEditDraft=false).
  const editable = c.allowedActions.canEditDraft;
  // смоук-фікс Igor: «підписаний скан» = наступний крок annex-петлі → та сама межа,
  // що canCreateAnnex (скан ≠ lifecycle-write дзеркала 1С; бекенд-гейт = contract-READ)
  const canUploadScan = c.allowedActions.canCreateAnnex || c.allowedActions.canUploadAttachment;

  async function onRowDownload(attachmentId: string, fileName: string) {
    setDownloadingId(attachmentId);
    setDownloadError(null);
    try {
      await downloadAttachment(attachmentDownloadUrl(c.id, attachmentId), fileName);
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : "Не вдалося завантажити");
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <button onClick={() => navigate(backTo)} className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground">
        <ChevronLeft className="h-4 w-4" />Договори
      </button>

      {/* header + lifecycle-timeline */}
      <div className="rounded-xl border bg-card p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-lg font-semibold">{type} {c.number && `№${c.number}`}</h1>
              <span className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${c.source === "sync_1c" ? "bg-muted text-muted-foreground" : "bg-primary/10 text-primary"}`}>
                {c.source === "sync_1c" ? "з 1С" : "CRM-керований"}
              </span>
            </div>
            <div className="mt-0.5 flex items-center gap-1.5 text-sm text-muted-foreground">
              <Building2 className="h-3.5 w-3.5" />
              {c.holding ? (
                <button onClick={() => navigate(`/clients/${c.holding!.id}`)} className="text-primary hover:underline">{client}</button>
              ) : <span>{client}</span>}
              {c.account?.name && c.account.name !== client && <span>· {c.account.name}</span>}
            </div>
          </div>
          {expiring && c.allowedActions.canRenew && (
            <button onClick={() => setRenewOpen(true)} className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-warning-foreground px-3 py-1.5 text-xs font-medium text-white">
              <AlertTriangle className="h-3.5 w-3.5" />Продовжити
            </button>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <TLStep label="Чернетка" done={!isDraft} current={isDraft} />
          <span className="text-muted-foreground/30">—</span>
          <TLStep label="Погодження" done={!isDraft && !isApproval} current={isApproval} />
          <span className="text-muted-foreground/30">—</span>
          <TLStep label="Підписано" done={TIMELINE_SIGNED.includes(st)} />
          <span className="text-muted-foreground/30">—</span>
          <TLStep label={isTerm ? "Розірвано/завершено" : "Активний"} done={TIMELINE_ACTIVE.includes(st)} current={isTerm} />
        </div>
      </div>

      {editable ? (
        /* Draft — editable terms surface (same component, three contexts). */
        <div className="mt-3 space-y-3">
          <Section title="Сторони" icon={<UserIcon className="h-4 w-4 text-primary" />}>
            {/* D-069: synthetic single-entity wrapper → hide the holding row; «Юрособа клієнта» already shows the client name. */}
            {!c.holding?.isSynthetic && <KV k="Клієнт (холдинг)" v={c.holding?.name ?? c.account?.name} />}
            <KV k="Юрособа клієнта" v={c.account?.name} />
            <KV k="Підписант" v={c.signedByContact?.name} empty="буде після підписання" />
          </Section>
          <DraftTermsEditor contract={c} />
        </div>
      ) : (
        /* Read-only — signed / approval / synced / sync_1c. */
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="space-y-3">
            <Section title="Сторони" icon={<UserIcon className="h-4 w-4 text-primary" />}>
              {/* D-069: synthetic single-entity wrapper → hide the holding row. */}
              {!c.holding?.isSynthetic && <KV k="Клієнт (холдинг)" v={c.holding?.name ?? c.account?.name} />}
              <KV k="Юрособа клієнта" v={c.account?.name} />
              <KV k="Підписант" v={c.signedByContact?.name} empty={isDraft || isApproval ? "буде після підписання" : "—"} />
            </Section>
            <CommercialTermsBlock terms={c.commercialTerms} onChangeRequest={() => setCtcOpen(true)} />
          </div>
          <div className="space-y-3">
            <Section title="Строки" icon={<Calendar className="h-4 w-4 text-primary" />}>
              <KV k="Підписано" v={c.signedDate} empty={isDraft || isApproval ? "ще не підписано" : "—"} />
              <KV k="Діє до" v={c.expirationDate} hl={expiring} />
            </Section>
            <Section title="Відповідальні" icon={<UserCheck className="h-4 w-4 text-primary" />}>
              <KV k="Наша юрособа" v={c.legalEntity?.name} empty="не визначено" />
              {c.legalEntity &&
                (c.legalEntity.status === "historical" ||
                  c.legalEntity.status === "inactive" ||
                  c.legalEntity.availableForNewContracts === false) && (
                  <p className="-mt-1 mb-1 rounded border border-warning-foreground/30 bg-warning px-2 py-1 text-[11px] text-warning-foreground">
                    {c.legalEntity.status === "historical" ? "Історична" : "Недоступна для нових"} — read-only, нові договори з нею не створюються.
                  </p>
                )}
              <KV k="Підписант (Kormax)" v={c.signedByKormaxDirector?.name ?? c.intendedKormaxSigner?.name} empty="—" />
            </Section>
          </div>
        </div>
      )}

      {/* documents */}
      <div className="mt-3">
        <Section title={`Документи (${c.attachments.length + (c.mainDocument ? 1 : 0)})`} icon={<Paperclip className="h-4 w-4 text-primary" />}>
          {c.mainDocument || c.attachments.length > 0 ? (
            <div className="space-y-1.5">
              {/* смоук-фікс Igor: рядки живі (§0) — імʼя = download (той самий байт, AC-14),
                  явна ⬇ праворуч, поруч наступна дія петлі «підписаний скан» (§20.6) */}
              {c.mainDocument?.versions?.[0] && (
                <DocRow
                  fileName={c.mainDocument.versions[0].fileName ?? "Основний документ"}
                  downloading={downloadingId === c.mainDocument.versions[0].attachmentId}
                  onDownload={() => void onRowDownload(c.mainDocument!.versions[0].attachmentId, c.mainDocument!.versions[0].fileName ?? "document")}
                />
              )}
              {c.attachments.map((a) => (
                <DocRow
                  key={a.attachmentId}
                  fileName={a.fileName}
                  meta={a.annexNumber
                    ? `№${a.annexNumber}${a.documentDate ? ` від ${a.documentDate.split("-").reverse().join(".")}` : ""}`
                    : undefined}
                  downloading={downloadingId === a.attachmentId}
                  onDownload={() => void onRowDownload(a.attachmentId, a.fileName)}
                  onUploadScan={canUploadScan ? () => setScanUploadOpen(true) : undefined}
                />
              ))}
            </div>
          ) : <div className="text-xs text-muted-foreground">Документів ще немає</div>}
          {downloadError && <p role="alert" className="mt-1.5 text-xs text-destructive">{downloadError}</p>}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {/* D-143 §0: жива CTA і на порожній секції; гейт canCreateAnnex (AC-1/AC-20) */}
            {c.allowedActions.canCreateAnnex && (
              <button
                onClick={() => setAnnexOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-primary/40 bg-primary/5 px-2.5 py-1 text-xs font-medium text-primary hover:bg-primary/10"
              >
                <FileText className="h-3 w-3" />Створити додаткову угоду
              </button>
            )}
            {c.allowedActions.canUploadAttachment && (
              <button disabled className="inline-flex cursor-not-allowed items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs text-muted-foreground/60" title="Завантаження — через картку дій">
                <Upload className="h-3 w-3" />Завантажити
              </button>
            )}
          </div>
        </Section>
      </div>

      <div className="mt-3 flex items-center gap-2 rounded-xl border bg-muted/40 px-4 py-2.5 text-xs text-muted-foreground">
        <ShieldCheck className="h-3.5 w-3.5" />Е-підпис (Vchasno) — відкладено; підписання поза CRM, статус оновлюється вручну.
      </div>

      {renewOpen && <RenewalDrawer open={renewOpen} onClose={() => setRenewOpen(false)} contractId={c.id} contractNumber={c.number} />}

      {/* смоук-фікс Igor: «Завантажити підписаний скан» — пресет signed_scan */}
      {scanUploadOpen && (
        <UploadDialog contractId={c.id} initialType="signed_scan" onClose={() => setScanUploadOpen(false)} />
      )}

      {/* D-143 — майстер додаткової угоди (ЕДО «Вчасно») */}
      {annexOpen && (
        <AnnexWizardModal
          contractId={c.id}
          holdingId={c.holding?.id ?? null}
          onClose={() => setAnnexOpen(false)}
        />
      )}

      {/* G1-S3 — terms change request (the only path to change sync_1c / locked terms).
          Carries the current cC* terms so the approver sees what is changing (Trap-48 both-set aware). */}
      {ctcOpen && (
        <CreateInternalRequestDrawer
          open={ctcOpen}
          onClose={() => setCtcOpen(false)}
          contractId={c.id}
          sourceModule="contract"
          sourceEntityId={c.id}
          initialRequestType="contract_terms_change"
          initialBody={currentTermsSummary(c)}
        />
      )}
    </div>
  );
}

// G1-S3 — current commercial terms summary for the change request body (cC* set,
// per composeCommercialTerms — same for crm_managed and sync_1c mirror).
function currentTermsSummary(c: ContractDetailResponse): string {
  const t = c.commercialTerms;
  const parts: string[] = [];
  if (t.deferralDays !== null) parts.push(`відстрочка ${t.deferralDays} дн`);
  if (t.creditLimit !== null) parts.push(`кредитний ліміт ${new Intl.NumberFormat("uk-UA").format(t.creditLimit)} ${t.currency || "₴"}`);
  if (t.priceTypeName) parts.push(`тип цін ${t.priceTypeName}`);
  const cur = parts.length ? parts.join(" · ") : "умови не задані";
  return `Договір ${c.number || c.id} (${c.source === "sync_1c" ? "з 1С" : "CRM"}).\nПоточні умови: ${cur}.\nПрошу змінити: `;
}
